import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/layout/Navbar';
import StockTicker from '../components/layout/StockTicker';
import Footer from '../components/layout/Footer';
import { ArticleCardStandard } from '../components/cards/ArticleCard';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Archive, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArchivePage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 12;

  useEffect(() => {
    fetchArchivedArticles(0, true);
  }, []);

  const fetchArchivedArticles = async (currentOffset, isInitial = false) => {
    if (isInitial) setLoading(true);
    else setLoadingMore(true);

    try {
      const response = await axios.get(`${API}/articles`, {
        params: { archived: true, limit, offset: currentOffset }
      });
      const newArticles = response.data;

      if (isInitial) {
        setArticles(newArticles);
      } else {
        setArticles((prev) => [...prev, ...newArticles]);
      }

      setHasMore(newArticles.length === limit);
    } catch (error) {
      console.error('Error fetching archived articles:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
    fetchArchivedArticles(newOffset, false);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="archive-page">
      <StockTicker />
      <Navbar />

      {/* Header */}
      <header className="py-12 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center space-x-3 mb-4">
            <Archive className="w-8 h-8 text-[#0F5132]" strokeWidth={1.5} />
            <h1
              className="text-4xl md:text-5xl font-bold text-gray-900"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="archive-title"
            >
              Archive
            </h1>
          </div>
          <p className="text-gray-500">
            Browse articles from our archive. These articles are older than 7 days and will be permanently removed after 30 days.
          </p>
        </div>
      </header>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="w-full h-[300px] rounded-sm" />
              ))}
            </div>
          ) : articles.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map((article) => (
                  <ArticleCardStandard key={article.id} article={article} />
                ))}
              </div>
              {hasMore && (
                <div className="mt-8 text-center">
                  <Button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="bg-[#0F5132] text-white hover:bg-[#0a3d25] font-semibold uppercase tracking-wide px-8"
                    data-testid="load-more-btn"
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      'Load More'
                    )}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <Archive className="w-16 h-16 text-gray-300 mx-auto mb-4" strokeWidth={1} />
              <p className="text-gray-500 text-lg mb-2">No archived articles</p>
              <p className="text-gray-400 text-sm">Articles older than 7 days will appear here.</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
